import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { buildInfo as dialerBuildInfo } from 'dialer';
import { BsModalService } from 'ngx-bootstrap/modal';
import { timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { buildInfo } from '../build';
import { AppConfig } from '../environments/environment';
import { AppState } from './core/app-state';
import { PathTestParticipant } from './core/models/path-test';
import { AuthenticationService, ElectronService, LookupService, PathTestService, ScoutPointPhotoService, ScoutPointPhotoUploadService, ScoutTaskService, ThemeService } from './core/services';
import { TestbedRxdbService } from './core/services/testbed-rxdb';
import { ConfirmModalComponent } from './shared/components/confirm-modal/confirm-modal.component';
import { VolunteerTaskPointViewService } from './volunteer/services/volunteer-task-point-view.service';

@Component({
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  @HostBinding('class')
  envThemeClass = ''

  constructor(
    public electronService: ElectronService,
    public appState: AppState,
    private authenticationService: AuthenticationService,
    lookupService : LookupService,
    pathTestService : PathTestService,
    scoutService : ScoutTaskService,
    volunteerTaskPointViewService : VolunteerTaskPointViewService,
    private testbedRxdbService : TestbedRxdbService,
    private scoutPointPhotoService : ScoutPointPhotoService,
    private scoutPointPhotoUploadService : ScoutPointPhotoUploadService,
    private sericeWorkerUpdate : SwUpdate,
    private modalService: BsModalService,
    private themeService: ThemeService,
  ) {
    console.log('%cTestbed Client:\n%c' +
      Object.keys(AppConfig).map(key=>` ❯ ${key}: ${JSON.stringify(AppConfig[key])}`).join('\n'),
      "font-size: 14px; color: #7c7c7b;", "font-size: 12px; color: #7c7c7b");
    console.log(`%c ❯ Build Version: ${buildInfo.version}\n` +
            ` ❯ Build Timestamp: ${buildInfo.timestamp}\n` +
            ` ❯ Git: %c${buildInfo.hash} (${buildInfo.user})`,
        "font-size: 12px; color: #7c7c7b",
        "font-size: 12px; color: #bdc6cf");
    console.log(`%c ❯ Dialer Build Version: ${dialerBuildInfo.version}\n` +
        ` ❯ Build Timestamp: ${dialerBuildInfo.timestamp}\n` +
        ` ❯ Git: %c${dialerBuildInfo.hash} (${dialerBuildInfo.user})`,
    "font-size: 12px; color: #7c7c7b",
    "font-size: 12px; color: #bdc6cf");
    
    scoutService.scoutPointModified$.subscribe( {
      next: async (scoutPointChange)=>{
          await pathTestService.handleScoutPointChange(scoutPointChange);
          await volunteerTaskPointViewService.handleScoutPointChange(scoutPointChange);
      }
    });

    this.envThemeClass = AppConfig.environment?.toLowerCase() + '-theme'

    if (appState.isElectron()) {
      console.log('\n***** Mode electron *****');
      //console.log(process.env);
      electronService.initApp();
      //console.log('Electron ipcRenderer', electronService.ipcRenderer);
      //console.log('NodeJS childProcess', electronService.childProcess);
    } else {
      console.log('\n***** Mode web *****');
      const appChannel = new BroadcastChannel('testbed-client-app');
      appChannel.postMessage('new-instance-notice');
      appChannel.addEventListener('message', (msg:MessageEvent) => {
        if (msg.data === 'new-instance-notice') {
          const msg = 'New application instance detected in this session.  Please exit one.'
          this.appState.publishError(msg,'APP-CHANNEL')
          this.appState.notifyError(msg,'APP-CHANNEL',2*60)
          appChannel.postMessage('existing-instance-reply')
        } else if ( msg.data === 'existing-instance-reply' ) {
          const msg = 'Existing application instance detected in this session.  Please exit one.'
          this.appState.publishError(msg,'APP-CHANNEL')
          this.appState.notifyError(msg,'APP-CHANNEL',2*60)
        } else if ( msg.data === 'closed-instance-notice' ) {
          const msg = 'Application instance closed.'
          this.appState.publishInfo(msg,'APP-CHANNEL',20)
          this.appState.notifyInfo(msg,'APP-CHANNEL',5)
        }
      });
    }

    this.authenticationService.currentUser.subscribe(user => {
      this.appState.setUser(user)
      if (user?.email) {
        this.appState.settings.setLastUserName(user.email)
      }
      this.electronService.updateLoginStatus()
    });

    // create websocket so server can notify this app of new data
    this.appState.webSocketSubject = webSocket(AppConfig.wsUrl);

    this.connectServerWebSocket()

    const pingInterval = this.appState.settings.getWsPingIntervalSecs() * 1000
    timer(0, pingInterval).subscribe(val => this.requestServerStatus())

    this.appState.appExitedSubject.pipe( filter(bExit=>bExit), take(1) ).subscribe( _ => {
      try {
        this.appState.webSocketSubject.unsubscribe()
        this.scoutPointPhotoUploadService.stop()
      } catch ( error ) {

      }
    })

    this.testbedRxdbService.doWhenReady( () => {
      this.scoutPointPhotoService.addReplicationStateListener(this.scoutPointPhotoService)
      this.testbedRxdbService.startSyncSubcriptions()
      this.scoutPointPhotoUploadService.start()
    })

    lookupService.doWhenReady( async () => {
      try {
          const pathTest : any = (await lookupService.rxCollection.findOne('pathTest').exec())?._data
          if ( pathTest?.participants ) {
              const participants : PathTestParticipant[] = pathTest?.participants
              pathTestService.participants = participants
              console.log("Assigning path test participants from CouchDB lookup")
          }
      } catch ( error : any ) {
          console.error(error)
          lookupService.publishError(error,this)
      }
  })
    //db.updatePendingUploadCount()
  }


  ngOnInit() {
    this.appState.publishInfo('Attempting server connection', null, 10)
    this.sericeWorkerUpdate.available.subscribe( () => {
      let initialState = {
        onYes: () => { location.reload() },
        title: "New Release Available",
        question: `A new Testbed client version is available.  `
          + `<div class="m-2 small font-italic text-danger"><i class='text-warning fa fa-exclamation-triangle mr-1'></i>Unsaved data may be lost during upgrade (ensure scout photos are uploaded)</div>`
          + `<div>Reload/upgrade now?</div>`,
        focusOnNo: true,
        yesStyle: 'btn btn-danger'
      }
      this.modalService.show(ConfirmModalComponent, {ignoreBackdropClick: true,initialState} );
    })
  }


  ngOnDestroy() {
    this.testbedRxdbService.stopSyncs()
  }

  connectServerWebSocket() {
    console.log('connectServerWebSocket()')
    this.appState.webSocketSubject.subscribe(
      msg => {
        // console.log('message received: ' + JSON.stringify(msg))
        this.appState.trackWebSocketReply(msg)
        try {
          if ( msg.type === 'dbEntityChanged') {
            this.appState.dbEntityChange$.next(msg);
          } else if ( msg.type === 'clientStats') {
            this.appState.clientStats$.next(msg.clientStats);
          } else {
            this.appState.webSocketMsg$.next(msg);
          }
        } catch ( error: any ) {
          console.error(error,msg);
        }
      },
      err => {
        //TODO - check for upload
        this.appState.trackWebSocketError(err)
        const secs = this.appState.settings.getWsReconnectDelaySecs() * 1000
        timer(secs).subscribe(val => this.connectServerWebSocket())
      },
      () => {
        console.log('app component websocket connection to server complete')
        this.appState.setOffline(true)
      }
    );
  }

  requestServerStatus() {
    this.appState.webSocketSubject.next({
      message: { type: 'serverState' },
      accessToken: this.appState.getUser()?.token
    })
  }
}
